import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, ArrowRight, Wine, Music, Utensils, Store, Sparkles, Bell, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import FaqPreview from '../components/FaqPreview';
import NotifyMeButton from '../components/NotifyMeButton';
import { getGuestFaqPreview, getOrganiserFaqPreview } from '../data/faq';
import { useAuth } from '../contexts/AuthContext';

import { BACKEND_URL } from '../lib/api';

const Homepage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // "List Event" / "Create Your Event" CTAs: an already-authenticated
  // organiser/admin (or associate) should go straight to their existing
  // landing page instead of being sent through registration again — mirrors
  // Header.js's per-role nav-link destinations exactly. Logged-out visitors
  // and customers keep the existing sign-up flow.
  const organiserCtaTarget = !user
    ? '/register?role=organiser'
    : user.role === 'associate'
      ? '/associate/events'
      : (user.role === 'organiser' || user.role === 'admin')
        ? '/dashboard'
        : '/register?role=organiser';

  useEffect(() => {
    fetchFeaturedEvents();
  }, []);

  const fetchFeaturedEvents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/events`);
      setFeaturedEvents(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    {
      name: 'Wine Tastings',
      icon: Wine,
      image: 'https://images.unsplash.com/photo-1764265930328-72716076e3ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHx3aW5lJTIwdGFzdGluZyUyMGVsZWdhbnR8ZW58MHx8fHwxNzc1NzM2NjgxfDA&ixlib=rb-4.1.0&q=85',
    },
    {
      name: 'Food & Pairings',
      icon: Utensils,
      // Elegant plated dining with wine — replaces the previous pouring shot
      // to avoid two near-identical wine-pour images next to "Wine Tastings".
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=940&q=80',
    },
    {
      name: 'Live Music',
      icon: Music,
      image: 'https://images.unsplash.com/photo-1770399883774-4a5ca3e32a7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxsaXZlJTIwbXVzaWMlMjBvdXRkb29yJTIwc3Vuc2V0fGVufDB8fHx8MTc3NTczNjY4Nnww&ixlib=rb-4.1.0&q=85',
    },
    {
      name: 'Markets & Pop-Ups',
      icon: Store,
      // Curated outdoor farmers' market with boutique stalls.
      image: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?auto=format&fit=crop&w=940&q=80',
    },
    {
      name: 'Lifestyle Events',
      icon: Sparkles,
      // Premium social gathering — table set under festoon lights.
      image: 'https://images.unsplash.com/photo-1530023367847-a683933f4172?auto=format&fit=crop&w=940&q=80',
    },
    {
      name: 'Special Events',
      icon: Star,
      // Stylish curated celebration — sparklers / candles.
      image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=940&q=80',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-[80vh] md:h-[90vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1602967217650-b8b1d1343609?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHx2aW5leWFyZCUyMGxhbmRzY2FwZSUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzc1NzM2Njg2fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto animate-fade-in">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-bold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="hero-title"
          >
            Find Your Next Experience with VinePass
          </h1>
          <p className="text-base leading-relaxed text-white/90 mb-8 max-w-2xl mx-auto">
            From wine tastings and farm events to food pairings, markets, festivals, private gatherings, family-friendly days out, and lifestyle experiences — explore and book memorable moments with VinePass.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/events')}
              className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-8 py-6 font-medium transition-all shadow-md text-lg"
              data-testid="browse-events-button"
            >
              Browse Experiences
            </Button>
            <Button
              onClick={() => navigate(organiserCtaTarget)}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1F3D2B] rounded-full px-8 py-6 font-medium transition-all text-lg"
              data-testid="list-event-button"
            >
              List Your Experience
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Events / Upcoming Experiences Section */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#FAFAF7]" data-testid="featured-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#112318] mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {!loading && featuredEvents.length === 0 ? 'Upcoming Experiences' : 'Featured Events'}
            </h2>
            <p className="text-base leading-relaxed text-[#4A5D50] max-w-2xl mx-auto">
              {!loading && featuredEvents.length === 0
                ? 'Curated wine farm experiences, coming soon to VinePass.'
                : 'Curated wine, food, lifestyle, and hospitality experiences worth booking.'}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#E5E9E6] overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-[#E5E9E6]" />
                  <div className="p-6 sm:p-8 space-y-3">
                    <div className="h-6 bg-[#E5E9E6] rounded w-3/4" />
                    <div className="h-4 bg-[#E5E9E6] rounded w-1/2" />
                    <div className="h-4 bg-[#E5E9E6] rounded w-2/3" />
                    <div className="h-10 bg-[#E5E9E6] rounded-full mt-4" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {featuredEvents.map((event) => (
                <div
                  key={event.event_id}
                  className="event-card bg-[#FAFAF7] border border-[#E5E9E6] shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/events/${event.event_id}`)}
                  data-testid={`event-card-${event.event_id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <h3
                      className="text-xl sm:text-2xl leading-snug font-medium text-[#112318] mb-3"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {event.title}
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-[#4A5D50]">
                        <Calendar className="w-4 h-4 mr-2" />
                        {event.date} at {event.time}
                      </div>
                      <div className="flex items-center text-sm text-[#4A5D50]">
                        <MapPin className="w-4 h-4 mr-2" />
                        {event.location}
                      </div>
                    </div>
                    <Button
                      className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full font-medium transition-all"
                      data-testid={`view-event-button-${event.event_id}`}
                    >
                      View Details
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Premium empty state — launch-ready, intentional, no fake content */
            <div
              className="max-w-3xl mx-auto bg-white border border-[#E5E9E6] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 sm:p-14 text-center"
              data-testid="empty-events-state"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F4F6F3] mb-6">
                <Sparkles className="w-7 h-7 text-[#1F3D2B]" />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-3 font-medium">
                Launching Soon
              </p>
              <h3
                className="text-2xl sm:text-3xl font-semibold text-[#112318] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Our first experiences are on their way.
              </h3>
              <p className="text-[#4A5D50] text-base leading-relaxed max-w-xl mx-auto mb-8">
                We&rsquo;re curating a beautiful collection of wine tastings, live music and
                vineyard picnics from South Africa&rsquo;s most loved farms. Be the first to
                know when bookings open.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <NotifyMeButton source="homepage_launch_cta" />
                <Button
                  onClick={() => navigate(organiserCtaTarget)}
                  variant="outline"
                  className="border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F4F6F3] rounded-full px-8 py-6 font-medium"
                  data-testid="list-first-event-button"
                >
                  List Your Event
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Browse by Experience Section */}
      <section className="py-16 sm:py-24 lg:py-32" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#112318] mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Browse by Experience
            </h2>
            <p className="text-base leading-relaxed text-[#4A5D50]">
              Find the perfect event for your taste
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <div
                  key={index}
                  className="relative group cursor-pointer rounded-2xl overflow-hidden h-64 shadow-lg hover:shadow-xl transition-all"
                  onClick={() => navigate(`/events?category=${encodeURIComponent(category.name)}`)}
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col items-center justify-end p-6">
                    <IconComponent className="w-8 h-8 text-white mb-2" strokeWidth={1.5} />
                    <h3 className="text-white text-lg font-semibold">{category.name}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Guest FAQ preview — sits between Browse by Experience and For Organisers */}
      <section className="py-16 sm:py-24 bg-[#F1EFE7] border-t border-[#E5E9E6]" data-testid="homepage-faq-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FaqPreview
            items={getGuestFaqPreview()}
            audience="guests"
            title="Common questions, quickly answered"
            subtitle="From booking to the QR code at the gate — everything you need to know."
            ctaLabel="Explore Experiences"
            ctaTo="/events"
            faqLinkTab="guests"
            testIdPrefix="homepage-guest-faq"
          />
        </div>
      </section>

      {/* For Organisers Section */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#1F3D2B]" data-testid="organisers-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            For Event Organisers
          </h2>
          <p className="text-base leading-relaxed text-white/90 mb-8 max-w-2xl mx-auto">
            List your wine farm events and reach thousands of enthusiasts looking for premium experiences.
            Simple event management, secure payments, and dedicated support.
          </p>
          <Button
            onClick={() => navigate(organiserCtaTarget)}
            className="bg-white text-[#1F3D2B] hover:bg-[#F4F6F3] rounded-full px-8 py-6 font-medium transition-all shadow-md text-lg"
            data-testid="create-event-cta-button"
          >
            Create Your Event
          </Button>

          {/* Organiser FAQ preview */}
          <div className="mt-16 sm:mt-20">
            <FaqPreview
              items={getOrganiserFaqPreview()}
              audience="organisers"
              title="Organiser questions, answered"
              subtitle="Payouts, fees, team scanning and onboarding — the essentials."
              ctaLabel="List Your Event"
              ctaTo={organiserCtaTarget}
              faqLinkTab="organisers"
              testIdPrefix="homepage-organiser-faq"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
